import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

interface CollectionItem {
  label: string;
  href: string;
  description: string;
}

interface CategoryItem {
  label: string;
  href: string;
  collections: CollectionItem[];
}

interface NavItem {
  label: string;
  href?: string;
  categories?: CategoryItem[];
}

const navItems: NavItem[] = [
  { label: "About Us", href: "/about-us" },
  {
    label: "Products",
    categories: [
      {
        label: "Hardwood",
        href: "/products/hardwood",
        collections: [
          { label: "The Solo Collection", href: "/products/hardwood/solo-collection", description: "Real Wood" },
          { label: "The Canto Collection", href: "/products/hardwood/canto-collection", description: "Custom Hardwood" },
        ],
      },
      {
        label: "Vinyl",
        href: "/products/vinyl",
        collections: [
          { label: "The Evo Collection", href: "/products/vinyl/evo-collection", description: "Luxury Vinyl Plank" },
        ],
      },
      {
        label: "Carpet",
        href: "/products/carpet",
        collections: [
          { label: "The Echo Collection", href: "/products/carpet/echo-collection", description: "Pattern Carpet" },
          { label: "The Verso Collection", href: "/products/carpet/verso-collection", description: "Texture Carpet" },
        ],
      },
      {
        label: "Laminate",
        href: "/products/laminate",
        collections: [
          { label: "The Novo Collection", href: "/products/laminate/novo-collection", description: "Laminated Wood" },
        ],
      },
      {
        label: "Tile",
        href: "/products/tile",
        collections: [
          { label: "The Flow Collection", href: "/products/tile/flow-collection", description: "Porcelain Tile" },
          { label: "The Hydro Collection", href: "/products/tile/hydro-collection", description: "Engineered Tile" },
        ],
      },
    ],
  },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState<string | null>(null);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveSubmenu(null);
    setActiveCategory(null);
    setMobileExpandedCategory(null);
  }, [location.pathname]);

  return (
    <>
      {/* Top Bar with Hours */}
      <div className="bg-secondary text-secondary-foreground py-2 text-sm">
        <div className="container-wide flex justify-between items-center">
          <p className="hidden sm:block">Mon-Fri: 10AM-6PM | Sat: 10AM-4PM | Sun: Closed</p>
          <a
            href="tel:+17043892166"
            className="flex items-center gap-2 hover:text-primary transition-colors ml-auto sm:ml-0"
          >
            <Phone className="h-4 w-4" />
            (704) 389-2166
          </a>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300",
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-md"
            : isHomePage
            ? "bg-transparent"
            : "bg-background"
        )}
      >
        <div className="container-wide">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/">
              <Logo variant={isScrolled || !isHomePage ? "dark" : "light"} />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.categories && setActiveSubmenu(item.label)}
                  onMouseLeave={() => {
                    setActiveSubmenu(null);
                    setActiveCategory(null);
                  }}
                >
                  {item.categories ? (
                    <button
                      className={cn(
                        "flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg",
                        isScrolled || !isHomePage
                          ? "text-foreground hover:text-primary hover:bg-muted"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                      )}
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  ) : (
                    <Link
                      to={item.href!}
                      className={cn(
                        "flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-lg",
                        isScrolled || !isHomePage
                          ? "text-foreground hover:text-primary hover:bg-muted"
                          : "text-white/90 hover:text-white hover:bg-white/10"
                      )}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Mega Menu Dropdown */}
                  <AnimatePresence>
                    {item.categories && activeSubmenu === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 top-full pt-2 z-50"
                      >
                        <div className="bg-background rounded-xl shadow-xl border border-border flex min-w-[500px]">
                          {/* Categories Column */}
                          <div className="w-48 border-r border-border p-2">
                            {item.categories.map((category) => (
                              <div
                                key={category.label}
                                onMouseEnter={() => setActiveCategory(category.label)}
                              >
                                <Link
                                  to={category.href}
                                  className={cn(
                                    "flex items-center justify-between px-4 py-3 text-sm rounded-lg transition-colors",
                                    activeCategory === category.label
                                      ? "bg-muted text-primary"
                                      : "text-foreground hover:bg-muted hover:text-primary"
                                  )}
                                >
                                  {category.label}
                                  <ChevronRight className="w-4 h-4" />
                                </Link>
                              </div>
                            ))}
                          </div>

                          {/* Collections Column */}
                          <div className="flex-1 p-4 min-h-[200px]">
                            {item.categories.map((category) => (
                              <AnimatePresence key={category.label}>
                                {activeCategory === category.label && (
                                  <motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.1 }}
                                    className="space-y-1"
                                  >
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-3">
                                      {category.label} Collections
                                    </p>
                                    {category.collections.map((collection) => (
                                      <Link
                                        key={collection.href}
                                        to={collection.href}
                                        className="block px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group"
                                      >
                                        <span className="block font-medium text-foreground group-hover:text-primary transition-colors">
                                          {collection.label}
                                        </span>
                                        <span className="block text-xs text-muted-foreground">
                                          {collection.description}
                                        </span>
                                      </Link>
                                    ))}
                                    <div className="pt-3 mt-3 border-t border-border">
                                      <Link
                                        to={category.href}
                                        className="text-sm text-primary hover:text-primary-dark font-medium"
                                      >
                                        View All {category.label} →
                                      </Link>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            ))}
                            {!activeCategory && (
                              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                                Hover over a category to see collections
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              <Button asChild variant="coral" size="default" className="hidden sm:flex">
                <Link to="/free-estimate">FREE ESTIMATE</Link>
              </Button>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-3 min-w-[44px] min-h-[44px] rounded-lg hover:bg-white/10 flex items-center justify-center"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <X className={cn("h-6 w-6", isScrolled || !isHomePage ? "text-foreground" : "text-white")} />
                ) : (
                  <Menu className={cn("h-6 w-6", isScrolled || !isHomePage ? "text-foreground" : "text-white")} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-background border-t border-border overflow-hidden"
            >
              <nav className="container-wide py-4 space-y-1" role="navigation" aria-label="Mobile navigation">
                {navItems.map((item) => (
                  <div key={item.label}>
                    {item.categories ? (
                      <div className="space-y-1">
                        <button
                          onClick={() => setActiveSubmenu(activeSubmenu === item.label ? null : item.label)}
                          className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium"
                        >
                          {item.label}
                          <ChevronDown className={cn("h-4 w-4 transition-transform", activeSubmenu === item.label && "rotate-180")} />
                        </button>
                        <AnimatePresence>
                          {activeSubmenu === item.label && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1"
                            >
                              {item.categories.map((category) => (
                                <div key={category.label}>
                                  <button
                                    onClick={() => setMobileExpandedCategory(mobileExpandedCategory === category.label ? null : category.label)}
                                    className="flex items-center justify-between w-full px-4 py-3 text-foreground hover:bg-muted rounded-lg"
                                  >
                                    {category.label}
                                    <ChevronDown className={cn("h-4 w-4 transition-transform", mobileExpandedCategory === category.label && "rotate-180")} />
                                  </button>
                                  <AnimatePresence>
                                    {mobileExpandedCategory === category.label && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="pl-4 space-y-1"
                                      >
                                        {category.collections.map((collection) => (
                                          <Link
                                            key={collection.href}
                                            to={collection.href}
                                            className="block px-4 py-3 min-h-[44px] rounded-lg hover:bg-muted transition-colors"
                                          >
                                            <span className="block text-foreground">{collection.label}</span>
                                            <span className="block text-xs text-muted-foreground">{collection.description}</span>
                                          </Link>
                                        ))}
                                        <Link
                                          to={category.href}
                                          className="block px-4 py-3 min-h-[44px] text-primary font-medium"
                                        >
                                          View All {category.label} →
                                        </Link>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href!}
                        className="flex items-center justify-between px-4 py-3 min-h-[44px] text-foreground hover:bg-muted rounded-lg"
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="pt-4 space-y-3">
                  <a
                    href="tel:+17043892166"
                    className="flex items-center gap-2 px-4 py-3 min-h-[44px] text-foreground hover:bg-muted rounded-lg"
                  >
                    <Phone className="h-4 w-4" />
                    (704) 389-2166
                  </a>
                  <Button asChild variant="coral" className="w-full min-h-[44px]">
                    <Link to="/free-estimate">
                      FREE ESTIMATE
                    </Link>
                  </Button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
